import React from 'react';
import pt from 'prop-types';
import { createPortal } from 'react-dom';
import { resolveLookupCollection } from '@swell/apps-sdk';
import { Label } from 'components/form';
import Icon from 'components/icon';
import DropdownButton from 'components/button/dropdown';
import Loading from 'components/loading';
import { StorefrontEditorSidebarIcon } from 'components/storefront/editor/icon';
import { EasyblocksBackendContext } from 'components/storefront/editor/backendContext';
import SwellLookupPanel from './lookup-panel';
import { singularize, classNames } from 'utils';
import {
  contentLookupRecordName,
  populateLookupValues,
  depopulateLookupValues,
} from 'utils/content';

import './lookup.scss';

export default class SwellLookup extends React.PureComponent {
  static contextType = EasyblocksBackendContext;

  static propTypes = {
    value: pt.any,
    field: pt.object,
    placeholder: pt.string,
    selectIcon: pt.string,
    record: pt.object,
    params: pt.object,
    onChange: pt.func,
    collection: pt.string,
    onQuery: pt.func,
    onClickEdit: pt.func,
    onClickSelect: pt.func,
    onLookupValue: pt.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      showPanel: false,
      collection: null,
      record: null,
      initialValue: props.value,
      loadingValue: false,
      isEditable: false,
    };
  }

  async componentDidMount() {
    await this.setInitialState();
  }

  async componentDidUpdate(prevProps) {
    if (this.props.value !== prevProps.value) {
      await this.setInitialState();
    }
  }

  async setInitialState(setValue = undefined) {
    const {
      params,
      collection: collectionProp,
      record: recordProp,
    } = this.props;

    const collection = collectionProp || resolveLookupCollection(params);

    this.setState({ loadingValue: true });

    const record =
      recordProp ?? (await this.getLookupValue(collection, setValue));

    this.setState({
      collection,
      record,
      loadingValue: false,
      isEditable: record && this.isEditable(collection),
    });
  }

  isEditable(collection) {
    const { onClickEdit } = this.props;

    switch (collection) {
      case 'menus':
      case 'categories':
        return true;
      default:
        return Boolean(onClickEdit);
    }
  }

  async getLookupValue(collection, setValue = undefined) {
    const { value: valueProp, params, onLookupValue } = this.props;

    const value = setValue ?? valueProp;

    if (onLookupValue) {
      return onLookupValue(value) || null;
    }

    // HACK: Special case for 'all' category
    // This should be replaced by a real "all" category via API
    if (collection === 'categories' && value === 'all') {
      return { slug: 'all', name: 'All products' };
    }

    const lookupValue = { value };
    await populateLookupValues(lookupValue, [
      {
        ...params,
        id: 'value',
        type: 'lookup',
        collection,
      },
    ]);

    return lookupValue.value || null;
  }

  onChangeLookup = (record) => {
    const { params, onChange } = this.props;
    const { collection } = this.state;

    const lookupValue = depopulateLookupValues({ record }, [
      {
        ...params,
        id: 'record',
        key: 'value',
        type: 'lookup',
        collection,
      },
    ]);

    onChange(lookupValue.value);

    this.setState({ record });
  };

  getNameValue(record) {
    const { collection } = this.state;
    if (record) {
      return contentLookupRecordName(collection, record);
    }
  }

  renderSelectValue() {
    const { value, selectIcon } = this.props;
    const { collection, record } = this.state;

    const renderedValue = this.getNameValue(record) || value;

    const icon = selectIcon ? (
      <Icon fa={selectIcon} />
    ) : (
      <StorefrontEditorSidebarIcon type={collection} />
    );

    return (
      <>
        {icon}
        {renderedValue}
      </>
    );
  }

  onClickSelect = () => {
    const { onClickSelect } = this.props;
    const { loadingValue } = this.state;

    if (loadingValue) {
      return;
    }

    if (onClickSelect) {
      onClickSelect(this);
    } else {
      this.setState({ showPanel: true });
    }
  };

  onUpdateStorefrontSettings() {
    const { onUpdateStorefrontSettings } = this.context;
    const { collection } = this.state;

    switch (collection) {
      case 'menus':
        return onUpdateStorefrontSettings(collection);
      default:
        return;
    }
  }

  modalUpdateRecord = async (record, created = false) => {
    const { onChange } = this.props;
    const { closeModal } = this.context;

    this.setState({ record });
    await this.onUpdateStorefrontSettings();
    onChange(record.id);
    closeModal();

    if (!created) {
      this.onCloseLookup();
    }
  };

  modalDeleteRecord = async () => {
    const { onChange } = this.props;
    const { closeModal } = this.context;

    this.setState({ record: null });
    await this.onUpdateStorefrontSettings();
    onChange(null);
    closeModal();

    this.onCloseLookup();
  };

  onClickEdit = () => {
    const { onClickEdit } = this.props;
    const { collection, record } = this.state;
    const { openModal } = this.context;

    if (!record?.id) {
      return;
    }

    if (onClickEdit) {
      onClickEdit(record);
      return;
    }

    switch (collection) {
      case 'categories': {
        openModal('EditCategory', {
          params: { id: record.id },
          deleteItemInline: true,
          onItemUpdated: this.modalUpdateRecord,
          onItemDeleted: this.modalDeleteRecord,
        });

        break;
      }

      case 'menus': {
        openModal('EditMenu', {
          record, // Pass the record directly
          params: { id: record.id },
          deleteItemInline: true,
          onItemUpdated: this.modalUpdateRecord,
          onItemDeleted: this.modalDeleteRecord,
        });

        break;
      }

      default:
        break;
    }
  };

  onClickNew = () => {
    const { collection } = this.state;
    const { openModal } = this.context;

    switch (collection) {
      case 'categories': {
        openModal('NewCategory', {
          createItemInline: true,
          onItemCreated: this.modalUpdateRecord,
        });

        break;
      }

      case 'menus': {
        openModal('NewMenu', {
          createItemInline: true,
          onItemCreated: (record) => {
            this.modalUpdateRecord(record, true);
          },
        });

        break;
      }

      default:
        break;
    }
  };

  onClickRemove = () => {
    const { onChange } = this.props;
    onChange(null);
  };

  onSaveLookup = () => {
    this.setState({ initialValue: this.props.value });
  };

  onCancelLookup = async () => {
    const { onChange } = this.props;
    const { initialValue } = this.state;

    await this.setInitialState(initialValue);

    onChange(initialValue);
  };

  onCloseLookup = () => {
    this.setState({ showPanel: false });
  };

  render() {
    const { value, field, placeholder, onQuery } = this.props;
    const { record, showPanel, collection, loadingValue, isEditable } =
      this.state;
    const { lookup } = this.context;

    const singleLabel = singularize(collection);

    return (
      <div className="storefront-editor-widget">
        <div className="form-field">
          <div className="form-lookup">
            <Label label={field.label} htmlFor={field.id} />
            <div className="form-field-input">
              {!loadingValue && value ? (
                <DropdownButton
                  button={false}
                  alignOffset={0}
                  sideOffset={6}
                  align="start"
                  items={[
                    <button key="select" onClick={this.onClickSelect}>
                      <span className="with-icon">
                        <Icon fa="arrow-pointer" />
                        Select {singleLabel}
                      </span>
                    </button>,
                    isEditable && (
                      <button key="edit" onClick={this.onClickEdit}>
                        <span className="with-icon">
                          <Icon fa="pen" />
                          Edit {singleLabel}
                        </span>
                      </button>
                    ),
                    <button
                      key="remove"
                      className="danger"
                      onClick={this.onClickRemove}
                    >
                      <span className="with-icon">
                        <Icon fa="trash" />
                        Remove {singleLabel}
                      </span>
                    </button>,
                  ]}
                >
                  <div
                    className={classNames('form-lookup-select', {
                      selected: true,
                    })}
                  >
                    {this.renderSelectValue()}
                  </div>
                </DropdownButton>
              ) : (
                <button
                  className="form-lookup-select"
                  onClick={this.onClickSelect}
                  type="button"
                >
                  {loadingValue ? (
                    <Loading size={18} />
                  ) : (
                    placeholder || 'Select'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {showPanel &&
          createPortal(
            <SwellLookupPanel
              field={field}
              value={value}
              record={record}
              collection={collection}
              lookup={lookup}
              onQuery={onQuery}
              onChange={this.onChangeLookup}
              onSave={this.onSaveLookup}
              onCancel={this.onCancelLookup}
              onClose={this.onCloseLookup}
              onClickNew={
                this.isEditable(collection) ? this.onClickNew : undefined
              }
            />,
            document.getElementById('sidebar-panels-root'),
          )}
      </div>
    );
  }
}
